import BaseBookingComponent from './baseBookingComponent';

export default class FormHotel extends BaseBookingComponent {
    whenLoaded = Promise.all([
        customElements.whenDefined('c-calendar'),
        customElements.whenDefined('calendar-range')
    ]);

    constructor(m) {
        super(m);

        // Additional properties and methods specific to FormHotel
        this.$addRoomCta = this.querySelector('[data-add-room]');
        this.$roomTemplate = this.querySelector('[data-room-template]');
        this.$datesDisplay = this.querySelector('[data-dates-display]');
        this.maxRooms = Number(this.getAttribute('data-max-rooms')) || 3;

        this.addRoom = this.addRoom.bind(this);
        this.removeRoom = this.removeRoom.bind(this);
    }

    // =============================================================================
    // Lifecycle
    // =============================================================================
    connectedCallback() {
        super.connectedCallback();

        this.setRoomTemplate();

        this.whenLoaded.then(() => {
            requestAnimationFrame(() => {
                this.onReset();
            });
        });
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }

    // =============================================================================
    // Events
    // =============================================================================
    bindEvents() {
        this.$addRoomCta.addEventListener('click', this.addRoom);
    }

    unbindEvents() {
        this.$addRoomCta.removeEventListener('click', this.addRoom);

        // Remove click listeners for room removal buttons
        const $removeRoomCtas = this.querySelectorAll('[data-remove-room]');
        $removeRoomCtas.forEach(($cta) => {
            $cta.removeEventListener('click', this.removeRoom);
        });
    }

    // =============================================================================
    // Callbacks
    // =============================================================================
    onCalendarChange(e) {
        this.setDateRangeValue();
    }

    onReset() {
        this.setDateRangeValue();
    }

    // =============================================================================
    // Methods
    // =============================================================================
    getCheckInDateField() {
        return 'hotel-dates[check-in]';
    }

    getCheckOutDateField() {
        return 'hotel-dates[check-out]';
    }

    getPropertyField() {
        return 'hotel-property';
    }

    setRoomTemplate() {
        this.roomTemplate = this.$roomTemplate.innerHTML;
        this.$roomTemplate.remove();
    }

    addRoom(e) {
        e.preventDefault();

        // Get the current list of rooms
        const $rooms = this.querySelectorAll('[data-room]');

        // Prevent adding more rooms if the limit is reached
        if (this.maxRooms && $rooms.length >= this.maxRooms) {
            return;
        }

        // Create a new room from the template
        const $tmpRoom = document.createElement('div');
        $tmpRoom.innerHTML = this.roomTemplate.trim();
        const $room = $tmpRoom.firstChild;

        // Bind the remove button event to the new room
        const $cta = $room.querySelector('[data-remove-room]');
        $cta?.addEventListener('click', this.removeRoom);
        this.fillRoomItem($room, $rooms.length + 1);

        // Add the new room to the form
        this.$formInner.appendChild($room);

        // Focus the first focusable element in the newly added room
        requestAnimationFrame(() => {
            const $focusableEls = $room.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            $focusableEls[0].focus();
        });

        // Disable the "Add Room" button if the maximum number of rooms is reached
        if (this.maxRooms && $rooms.length + 1 >= this.maxRooms) {
            this.$addRoomCta.setAttribute('disabled', 'true');
            this.$addRoomCta.setAttribute('aria-disabled', 'true');
        }
    }

    removeRoom(e) {
        const $cta = e.target;

        // Find the room associated with the button
        const $room = $cta.closest('[data-room]');
        $cta?.removeEventListener('click', this.removeRoom);

        // Remove the room from the DOM
        $room.remove();
        this.updateRooms();

        // Focus the last room's first focusable element
        requestAnimationFrame(() => {
            const $lastRoom = Array.from(this.querySelectorAll('[data-room]')).pop();
            const $focusableEls = $lastRoom.querySelectorAll(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            $focusableEls[0].focus();
        });

        // Re-enable the "Add Room" button if the number of rooms is below the maximum
        if (this.maxRooms && this.querySelectorAll('[data-room]').length < this.maxRooms) {
            this.$addRoomCta.removeAttribute('disabled');
            this.$addRoomCta.removeAttribute('aria-disabled');
        }
    }

    updateRooms() {
        // Re-index room-related attributes (ID, name, etc.) after removing a room
        const $rooms = Array.from(this.querySelectorAll('[data-room]')).slice(1);
        $rooms.forEach(($room, index) => {
            $room.querySelectorAll('[id],[for],[name],[data-room-index]').forEach(($el) => {
                if ($el.id) {
                    $el.id = $el.id.replace(/\d+/, index + 2);
                }

                if ($el.htmlFor) {
                    $el.htmlFor = $el.htmlFor.replace(/\d+/, index + 2);
                }

                if ($el.name) {
                    $el.name = $el.name.replace(/\d+/, index + 2);
                }

                if ($el.dataset.roomIndex != null) {
                    const textContent = $el.textContent.replace(/\d+/, index + 2);
                    $el.textContent = textContent;
                }
            });
        });
    }

    fillRoomItem($room, newIndex) {
        // Populate placeholders in the room template with the correct room index
        $room.querySelectorAll('[id],[for],[name],[data-room-index]').forEach(($el) => {
            if ($el.id) {
                $el.id = $el.id.replace('{index}', newIndex);
            }

            if ($el.htmlFor) {
                $el.htmlFor = $el.htmlFor.replace('{index}', newIndex);
            }

            if ($el.name) {
                $el.name = $el.name.replace('{index}', newIndex);
            }

            if ($el.dataset.roomIndex != null) {
                const textContent = $el.textContent.replace('{index}', newIndex);
                $el.textContent = textContent;
            }
        });
    }

    appendRoomDataToUrl(url, data) {
        const rooms = Array.from(data.entries()).filter(
            ([key]) => key.startsWith('hotel-guests[room-') && key.endsWith('][adults]')
        ).length;

        if (rooms === 1) {
            const adults = data.get('hotel-guests[room-1][adults]');
            const children = data.get('hotel-guests[room-1][children]');
            if (adults) url.searchParams.append('adults', adults);
            if (children) url.searchParams.append('children', children);
        } else {
            url.searchParams.append('rooms', rooms);
            const totalAdults = this.sumGuestValues(data, 'adults');
            const totalChildren = this.sumGuestValues(data, 'children');

            url.searchParams.append('adults', totalAdults);
            url.searchParams.append('children', totalChildren);
        }
    }

    sumGuestValues(data, type) {
        return Array.from(data.entries())
            .filter(([key]) => key.endsWith(`][${type}]`))
            .reduce((acc, [, value]) => acc + Number(value), 0);
    }

    resetRooms() {
        // Remove all rooms except the first
        const $rooms = Array.from(this.querySelectorAll('[data-room]')).slice(1);
        $rooms.forEach(($room) => {
            const $cta = $room.querySelector('[data-remove-room]');
            $cta?.removeEventListener('click', this.removeRoom);
            $room.remove();
        });

        // Re-enable the "Add Room" button
        this.$addRoomCta.removeAttribute('disabled');
        this.$addRoomCta.removeAttribute('aria-disabled');
    }

    setDateRangeValue() {
        if (!this.$calendar) {
            return;
        }
        this.$datesDisplay.innerHTML = this.formatDateRange(this.$calendar?.value);
    }
}
